import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ContentProvider, useContent } from './contexts/ContentContext';
import AdminLogin from './admin/AdminLogin';
import AdminCallback from './admin/AdminCallback';
import AdminDashboard from './admin/AdminDashboard';

// Scroll to Top Component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' // 'smooth' yerine 'instant' kullanarak anında scroll
    });
  }, [pathname]);

  return null;
};

// Navbar Component
const Navbar = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  
  return (
    <nav className="navbar">
      <div className="container">
        <Link to="/" className="logo" onClick={() => setIsMobileMenuOpen(false)}>
          <img src="/logo.png" alt="ELIT IPTV" className="logo-img" />
          <span className="logo-text">ELIT IPTV</span>
        </Link>
        <button 
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        {isMobileMenuOpen && (
          <div 
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
        <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          <li><Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Ana Sayfa</Link></li>
          <li><Link to="/paketler" className={`nav-link ${location.pathname === '/paketler' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Paketler</Link></li>
          <li><Link to="/bayilik" className={`nav-link ${location.pathname === '/bayilik' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Bayilik</Link></li>
          <li><Link to="/kurulum" className={`nav-link ${location.pathname === '/kurulum' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Kurulum</Link></li>
          <li><Link to="/hakkimizda" className={`nav-link ${location.pathname === '/hakkimizda' ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>Hakkımızda</Link></li>
        </ul>
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const { contactInfo } = useContent();
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  const address = contactInfo?.address || { city: 'Berlin', country: 'Almanya' };
  const email = contactInfo?.email || 'elitelk2009@gmail.com';
  const whatsapp = contactInfo?.whatsapp || '+49 163 8926744';

  const handleLogoClick = (e) => {
    e.preventDefault();
    const now = Date.now();
    
    // 3 saniye içindeki tıklamaları say
    if (now - lastClickTime > 3000) {
      setClickCount(1);
    } else {
      setClickCount(prev => prev + 1);
    }
    
    setLastClickTime(now);

    // 8 tıklamada admin paneline yönlendir
    if (clickCount + 1 === 8) {
      window.location.href = '/admin/login';
      setClickCount(0);
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <div onClick={handleLogoClick} className="logo-link" style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="ELIT IPTV" className="logo-img" />
              <span className="logo-text">ELIT IPTV</span>
            </div>
            <p>Dünya standartlarında IPTV deneyimi. Binlerce kanal, film ve dizi içeriği ile eğlence her an yanınızda.</p>
          </div>
          <div className="footer-links">
            <h4>Hızlı Linkler</h4>
            <ul>
              <li><Link to="/">Ana Sayfa</Link></li>
              <li><Link to="/paketler">Paketler</Link></li>
              <li><Link to="/bayilik">Bayilik</Link></li>
              <li><Link to="/kurulum">Kurulum</Link></li>
              <li><Link to="/iletisim">İletişim</Link></li>
              <li><Link to="/sss">SSS</Link></li>
            </ul>
          </div>
          <div className="footer-contact">
            <h4>İletişim</h4>
            <ul className="contact-info">
              <li><span>📍</span> {address.city}, {address.country}</li>
              <li><span>✉</span> {email}</li>
              <li><span>📞</span> {whatsapp}</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2024 Elit IPTV. Tüm hakları saklıdır.</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const [currentText, setCurrentText] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const { homeContent, contactInfo, links } = useContent();
  
  // Default links (fallback)
  const defaultLinks = {
    windowsPC: 'https://tinyurl.com/elitpc1',
    androidXC: 'https://tinyurl.com/elitmedia1',
    androidSmarters: 'https://tinyurl.com/elitmedia2',
    playStoreXC: 'https://play.google.com/store/apps/details?id=com.nathnetwork.elitplus',
    playStoreSmarters: 'https://play.google.com/store/apps/details?id=com.elitdigitalone.elitdigitaliptvbox',
    iosApp: 'https://apps.apple.com/us/app/000-player/id1665441224'
  };
  const appLinks = links || defaultLinks;
  
  // Default values
  const defaultHeroTexts = [
    { line1: 'KESİNTİSİZ YAYIN', line2: '4K KALİTE' },
    { line1: 'GÜÇLÜ ALTYAPI', line2: 'HIZLI ERİŞİM' },
    { line1: 'SINIRSIZ EĞLENCE', line2: 'HER CİHAZDA' }
  ];

  const heroTexts = homeContent?.heroTexts || defaultHeroTexts;
  const heroDescription = homeContent?.heroDescription || 'Binlerce içeriğe anında erişin. Türkiye\'n en güçlü IPTV altyapısı.';
  const stats = homeContent?.stats || { channels: '45.000+', movies: '72.223+', series: '25.285+' };
  const features = homeContent?.features || [
    { icon: '🎬', title: 'Zengin İçerik', description: 'En güncel filmler, popüler diziler ve dünya çapında binlerce canlı kanal.' },
    { icon: '⚡', title: 'Hızlı Aktivasyon', description: 'Ödemeniz onaylandığı an üyeliğiniz aktif edilir. Anında izlemeye başlayın.' },
    { icon: '🛡️', title: 'Kesintisiz Yayın', description: 'Anti-Freeze teknolojimiz ile donma ve takılma yaşamadan izleyin.' }
  ];
  const featuredPackage = homeContent?.featuredPackage || {
    name: '6 AY PAKET',
    price: '1.300',
    originalPrice: '2.000',
    features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu']
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % heroTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [heroTexts.length]);

  const filmPosters = [
    'https://m.media-amazon.com/images/M/MV5BNTc4ODc5ODYtOWE0Zi00YjBiLTkwOGEtMDY3OGRhMDlkY2QyXkEyXkFqcGc@._V1_.jpg',
    'https://m.media-amazon.com/images/M/MV5BZGRlMDA5NGYtOWNmYy00YWI3LWFlMzMtYjlhMTkxZmI1NDRjXkEyXkFqcGc@._V1_QL75_UX190_CR0,0,190,281_.jpg',
    'https://m.media-amazon.com/images/M/MV5BMWQ4YWYxYTAtZTlhNC00Nzc3LWE3OWUtZjY5MThlNWNiYTBiXkEyXkFqcGc@._V1_QL75_UX190_CR0,0,190,281_.jpg',
    'https://m.media-amazon.com/images/M/MV5BNzQxNDYyNTQtYjg1OC00OGJiLTk2YjMtZjZjZDgwOWFlOTJjXkEyXkFqcGc@._V1_FMjpg_UX1000_.jpg',
    'https://m.media-amazon.com/images/M/MV5BYmFlMzk2N2MtN2YxNC00OGY0LWJmNjQtN2NlYmI4NWUwZWU3XkEyXkFqcGc@._V1_.jpg',
    'https://m.media-amazon.com/images/M/MV5BMTNhMDJmNmYtNDQ5OS00ODdlLWE0ZDAtZTgyYTIwNDY3OTU3XkEyXkFqcGc@._V1_FMjpg_UX1000_.jpg',
    'https://m.media-amazon.com/images/M/MV5BZDYxY2I1OGMtN2Y4MS00ZmU1LTgyNDAtODA0MzAyYjI0N2Y2XkEyXkFqcGc@._V1_FMjpg_UX1000_.jpg',
    'https://m.media-amazon.com/images/M/MV5BYmY3MmRhMWMtY2FhNS00M2VmLWFmNzEtYTMyZWU0OTVlYzQ4XkEyXkFqcGc@._V1_.jpg',
    'https://m.media-amazon.com/images/M/MV5BMGNiN2RlZTMtMTkyZC00YjkwLTgyY2QtMDg1ZDNhODQwNWM4XkEyXkFqcGc@._V1_.jpg',
    'https://m.media-amazon.com/images/M/MV5BMWYzZTM5ZGQtOGE5My00NmM2LWFlMDEtMGNjYjdmOWM1MzA1XkEyXkFqcGc@._V1_.jpg'
  ];

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Elit IPTV",
    "url": "https://elitiptv.net",
    "logo": "https://elitiptv.net/logo.png",
    "description": "Türkiye'nin en güçlü IPTV altyapısı. 50.000+ canlı TV kanalı, 72.000+ film ve dizi arşivi. 4K kalite, kesintisiz yayın.",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+49-163-892-6744",
      "contactType": "customer service",
      "availableLanguage": ["Turkish", "English", "German"]
    },
    "sameAs": [
      "https://play.google.com/store/apps/details?id=com.elitdigitalone.elitdigitaliptvbox"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "5000"
    }
  };

  return (
    <div className="home">
      <SEO
        title="Elit IPTV | Premium IPTV Hizmeti - 50.000+ Kanal, 4K Kalite"
        description="Elit IPTV ile 50.000+ canlı TV kanalı, 72.000+ film ve dizi arşivine erişin. 4K Ultra HD kalite, kesintisiz yayın, tüm cihazlarda uyumlu. Türkiye ve Avrupa'nın en güçlü IPTV altyapısı. Ücretsiz test fırsatı!"
        keywords="iptv, iptv türkiye, iptv paket, canlı tv, film izle, dizi izle, 4k iptv, smart tv iptv, android iptv, iptv abonelik, premium iptv, elit iptv, iptv hizmeti, iptv fiyat"
        url="/"
        structuredData={homeStructuredData}
      />
      <Navbar />
      
      <section className="hero">
        <div className="hero-background">
          <div className="bg-gradient-animated"></div>
          <div className="floating-shapes">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="floating-shape" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${i * 2}s`,
                animationDuration: `${15 + i * 5}s`
              }}></div>
            ))}
          </div>
        </div>
        <div className="container">
          <div className="hero-container">
            <div className="hero-header">
              <div className="hero-title-wrapper">
                {heroTexts.map((textObj, index) => (
                  <h1 
                    key={`${textObj.line1}-${textObj.line2}`}
                    className={`hero-title ${currentText === index ? 'active' : ''}`}
                  >
                    <span className="hero-highlight">
                      <span className="hero-line-1">{textObj.line1}</span>
                      <span className="hero-line-2">{textObj.line2}</span>
                    </span>
                  </h1>
                ))}
              </div>
              <p className="hero-desc">{heroDescription}</p>
              <div className="hero-actions">
                <Link to="/paketler" className="hero-btn hero-btn-primary">Paketleri İncele</Link>
                <button 
                  className="hero-btn hero-btn-secondary"
                  onClick={() => {
                    setIsTestMode(true);
                    setIsModalOpen(true);
                  }}
                >
                  Ücretsiz Test Et
                </button>
              </div>
            </div>
            <div className="hero-stats">
              <div className="stat-box">
                <div className="stat-icon stat-icon-orange">📺</div>
                <div className="stat-content">
                  <div className="stat-num">{stats.channels}</div>
                  <div className="stat-text">CANLI TV KANALI</div>
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-icon stat-icon-purple">🎬</div>
                <div className="stat-content">
                  <div className="stat-num">{stats.movies}</div>
                  <div className="stat-text">VOD FİLM ARŞİVİ</div>
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-icon stat-icon-green">📱</div>
                <div className="stat-content">
                  <div className="stat-num">{stats.series}</div>
                  <div className="stat-text">DİZİ ARŞİVİ</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sports-carousel">
        <div className="container">
          <div className="carousel-header">
            <h2>Sinema Şöleni</h2>
            <p>Binlerce güncel film ve dizi parmaklarınızın ucunda</p>
          </div>
        </div>
        <div className="carousel-wrapper">
          <div className="carousel-track">
            {[...filmPosters, ...filmPosters, ...filmPosters].map((poster, index) => (
              <img 
                key={index} 
                src={poster} 
                alt={`Film ${index + 1}`}
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        </div>
      </section>

      <section className="highlight-features">
        <div className="container">
          <div className="features-grid-simple">
            {features.map((feature, index) => (
              <div key={index} className="feature-card-simple">
                <div className="feature-icon-large">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="package-preview-home">
        <div className="container">
          <h2 className="section-title-center"><span className="gradient-text">En Popüler Başlangıç Paketi</span></h2>
          <p className="section-subtitle">Hemen deneyin, kalitemizi kendiniz görün.</p>
          
          <div className="package-card-featured">
            <div className="package-badge">EN ÇOK TERCİH EDİLEN</div>
            <h3>{featuredPackage.name}</h3>
            <div className="package-price">{featuredPackage.price}<span>TL</span></div>
            {featuredPackage.originalPrice && (
              <div className="package-original-price">{featuredPackage.originalPrice} TL</div>
            )}
            <ul className="package-features">
              {featuredPackage.features.map((feature, index) => (
                <li key={index}>✓ {feature}</li>
              ))}
            </ul>
            <Link to="/paketler" className="btn-primary">Daha Fazla Paket Gör</Link>
          </div>
        </div>
      </section>

      <section className="official-apps">
        <div className="container">
          <h2 className="section-title-center"><span className="gradient-text">Resmi Uygulamalarımız</span></h2>
          <p className="section-subtitle-large">APK dosyalarına veda edin! Güvenliğiniz için sadece resmi mağazalardaki uygulamalarımızı kullanın.</p>
          
          <div className="apps-grid">
            <div className="app-card">
              <div className="app-icon">🤖</div>
              <h4>Android & TV</h4>
              <p>Tüm Android telefon, tablet ve TV Box cihazlarınız için.</p>
              <div className="app-buttons">
                <a href="https://play.google.com/store/apps/details?id=com.elitdigitalone.elitdigitaliptvbox" target="_blank" rel="noopener noreferrer" className="store-btn-modern">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" />
                  ELIT Smarters
                </a>
                <a href={appLinks.androidSmarters} target="_blank" rel="noopener noreferrer" className="store-btn-modern">
                  ELIT Smarters (Harici İndir)
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.nathnetwork.elitplus" target="_blank" rel="noopener noreferrer" className="store-btn-modern">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" />
                  ELIT XC
                </a>
                <a href={appLinks.androidXC} target="_blank" rel="noopener noreferrer" className="store-btn-modern">
                  ELIT XC (Harici İndir)
                </a>
              </div>
            </div>
            
            <div className="app-card">
              <div className="app-icon">🍎</div>
              <h4>iOS & Apple TV</h4>
              <p>iPhone, iPad ve Apple TV cihazlarınız için resmi uygulama.</p>
              <div className="app-buttons">
                <a href="https://apps.apple.com/us/app/000-player/id1665441224" target="_blank" rel="noopener noreferrer" className="store-btn-modern">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="App Store" />
                  000 Player
                </a>
              </div>
            </div>
            
            <div className="app-card">
              <div className="app-icon">💻</div>
              <h4>Windows PC</h4>
              <p>Bilgisayarınızdan yüksek performanslı izleme deneyimi.</p>
              <div className="app-buttons">
                <a href={appLinks.windowsPC} target="_blank" rel="noopener noreferrer" className="store-btn-modern">
                  🪟 Windows İçin İndir
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <div className="whatsapp-float">
        <button 
          className="whatsapp-btn" 
          onClick={() => sendSmartWhatsAppMessage('general', {}, contactInfo)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" />
        </button>
      </div>

      <OrderModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsTestMode(false);
        }}
        packageInfo={isTestMode ? { name: 'Ücretsiz Test', duration: '24 Saat', price: 'Ücretsiz' } : null}
        isTest={isTestMode}
      />
    </div>
  );
};

// Akıllı WhatsApp Mesaj Fonksiyonu
const sendSmartWhatsAppMessage = (type, data = {}, contactInfo = null) => {
  // ContactInfo'dan WhatsApp numarasını al, yoksa default kullan
  const whatsappNumber = contactInfo?.whatsapp?.replace(/[^0-9]/g, '') || '491638926744';
  let message = '';
  
  switch(type) {
    case 'general':
      message = `*MERHABA! 👋*\n\nElit IPTV hakkında bilgi almak istiyorum.\n\n`;
      if (data.question) message += `*Sorum:* ${data.question}\n`;
      break;
      
    case 'package':
      message = `*PAKET SATIN ALMA TALEBİ 📦*\n\n`;
      if (data.packageName) message += `*Paket:* ${data.packageName}\n`;
      if (data.duration) message += `*Süre:* ${data.duration}\n`;
      if (data.price) message += `*Fiyat:* ${data.price}\n`;
      message += `\nBu paketi satın almak istiyorum. Detaylı bilgi alabilir miyim?`;
      break;
      
    case 'test':
      message = `*ÜCRETSİZ TEST TALEBİ 🎁*\n\n`;
      message += `Merhaba, Elit IPTV'yi ücretsiz test etmek istiyorum.\n\n`;
      if (data.device) message += `*Cihazım:* ${data.device}\n`;
      if (data.question) message += `*Sorum:* ${data.question}\n`;
      break;
      
    case 'installation':
      message = `*KURULUM DESTEĞİ TALEBİ 🔧*\n\n`;
      message += `Merhaba, cihazımda kurulum desteği almak istiyorum.\n\n`;
      if (data.device) message += `*Cihaz:* ${data.device}\n`;
      if (data.tvBrand) message += `*TV Markası:* ${data.tvBrand}\n`;
      if (data.app) message += `*Kullanılan Uygulama:* ${data.app}\n`;
      if (data.question) message += `*Sorunum:* ${data.question}\n`;
      break;
      
    case 'reseller':
      message = `*BAYİLİK BAŞVURUSU 💼*\n\n`;
      message += `Merhaba, Elit IPTV bayilik programı hakkında bilgi almak istiyorum.\n\n`;
      if (data.level) message += `*İlgilendiğim Seviye:* ${data.level}\n`;
      if (data.experience) message += `*Deneyimim:* ${data.experience}\n`;
      if (data.question) message += `*Sorum:* ${data.question}\n`;
      break;
      
    case 'support':
      message = `*TEKNİK DESTEK TALEBİ 🆘*\n\n`;
      message += `Merhaba, teknik destek almak istiyorum.\n\n`;
      if (data.issue) message += `*Sorunum:* ${data.issue}\n`;
      if (data.device) message += `*Cihaz:* ${data.device}\n`;
      if (data.details) message += `*Detaylar:* ${data.details}\n`;
      break;
      
    default:
      message = `*MERHABA! 👋*\n\nElit IPTV hakkında bilgi almak istiyorum.`;
  }
  
  message += `\n\n*Sayfa:* ${window.location.pathname}`;
  message += `\n*Tarih:* ${new Date().toLocaleDateString('tr-TR')}`;
  
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
  window.open(whatsappUrl, '_blank');
};

// SEO Component
const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  structuredData 
}) => {
  const siteUrl = 'https://elitiptv.net';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const fullImage = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : `${siteUrl}/logo.png`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Elit IPTV" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="Turkish" />
      <meta name="revisit-after" content="7 days" />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Elit IPTV" />
      <meta property="og:locale" content="tr_TR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional SEO */}
      <meta name="theme-color" content="#8B5CF6" />
      <meta name="msapplication-TileColor" content="#8B5CF6" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'active' : ''}`}>
      <div className="faq-header" onClick={() => setIsOpen(!isOpen)}>
        <h4>{question}</h4>
        <div className="faq-icon">{isOpen ? '−' : '+'}</div>
      </div>
      {isOpen && (
        <div className="faq-content">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

// Order Modal Component
const OrderModal = ({ isOpen, onClose, packageInfo, isTest = false }) => {
  const { contactInfo } = useContent();
  const [selectedDevice, setSelectedDevice] = useState('smart-tv');
  const [tvBrand, setTvBrand] = useState('');
  const [appUsed, setAppUsed] = useState('');
  const [username, setUsername] = useState('');
  const [note, setNote] = useState('');

  const devices = [
    { id: 'smart-tv', name: 'Smart TV', icon: '📺' },
    { id: 'android-tv', name: 'Android TV', icon: '🤖' },
    { id: 'mobil', name: 'Mobil', icon: '📱' },
    { id: 'pc-mac', name: 'PC/Mac', icon: '💻' },
    { id: 'mag-box', name: 'Mag/Box', icon: '📦' }
  ];

  const handleWhatsApp = () => {
    const deviceName = devices.find(d => d.id === selectedDevice)?.name || selectedDevice;
    const packageName = packageInfo?.name || 'Paket';
    const packagePrice = packageInfo?.price || '';
    const packageDuration = packageInfo?.duration || '';
    
    let message = `*${isTest ? 'ÜCRETSİZ TEST' : 'PAKET SATIN ALMA'} TALEBİ*\n\n`;
    message += `📦 *Paket:* ${packageName}\n`;
    if (packageDuration) message += `⏱️ *Süre:* ${packageDuration}\n`;
    if (packagePrice) message += `💰 *Fiyat:* ${packagePrice}\n\n`;
    message += `📱 *Cihaz:* ${deviceName}\n`;
    if (tvBrand) message += `📺 *TV Markası:* ${tvBrand}\n`;
    if (appUsed) message += `🔧 *Kullanılan Uygulama:* ${appUsed}\n`;
    if (username) message += `👤 *Kullanıcı Adı:* ${username}\n`;
    if (note) message += `📝 *Not:* ${note}\n`;
    
    const whatsappNumber = contactInfo?.whatsapp?.replace(/[^0-9]/g, '') || '491638926744';
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2 className="modal-title">Paket Seçimi</h2>
        <p className="modal-subtitle">Lütfen bilgilerinizi giriniz.</p>
        
        <div className="modal-form">
          <div className="form-section">
            <label className="form-label">Cihaz Seçin</label>
            <div className="device-grid">
              {devices.map(device => (
                <div
                  key={device.id}
                  className={`device-card ${selectedDevice === device.id ? 'active' : ''}`}
                  onClick={() => setSelectedDevice(device.id)}
                >
                  <div className="device-icon">{device.icon}</div>
                  <div className="device-name">{device.name}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-icon">📺</span> TV Markası
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Örn: Samsung, LG, Philips"
              value={tvBrand}
              onChange={(e) => setTvBrand(e.target.value)}
            />
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-icon">🔧</span> Kullanılan Uygulama
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Örn: Smarters, VLC, Netiptv"
              value={appUsed}
              onChange={(e) => setAppUsed(e.target.value)}
            />
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-icon">👤</span> Kullanıcı Adı (Tercihiniz)
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Örn: elit_user"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="form-section">
            <label className="form-label">
              <span className="label-icon">📝</span> Notunuz (Opsiyonel)
            </label>
            <textarea
              className="form-textarea"
              placeholder="Varsa özel bir isteğiniz..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows="4"
            />
          </div>

          <button className="btn btn-primary btn-full btn-whatsapp" onClick={handleWhatsApp}>
            <span>💬</span> WhatsApp ile İlet
          </button>
        </div>
      </div>
    </div>
  );
};

// Hakkımızda Page
const Hakkimizda = () => {
  const { aboutContent, contactInfo } = useContent();
  
  // Default values
  const story = aboutContent?.story || 'Elit IPTV, 2020 yılında Berlin, Almanya\'da kurulmuş, dünya genelinde hizmet veren bir IPTV sağlayıcısıdır. Misyonumuz, kullanıcılarımıza en kaliteli, kesintisiz ve uygun fiyatlı IPTV deneyimini sunmaktır.';
  const story2 = aboutContent?.story2 || 'Yüksek hızlı sunucularımız, gelişmiş teknoloji altyapımız ve 7/24 müşteri desteği ile binlerce mutlu müşteriye hizmet vermekteyiz. Türkiye ve Avrupa bölgesinde özel optimizasyonlar yaparak, en iyi izleme deneyimini garanti ediyoruz.';
  const story3 = aboutContent?.story3 || 'Modern teknoloji, güvenli ödeme sistemleri ve kullanıcı odaklı yaklaşımımız ile sektörde öncü bir konumdayız. Her geçen gün kendimizi geliştirerek, müşterilerimize daha iyi hizmet sunmak için çalışıyoruz.';
  const values = aboutContent?.values || [
    { icon: '🎯', title: 'Güvenilirlik', description: '%99.9 uptime garantisi ile kesintisiz hizmet sunuyoruz. Sunucularımız 7/24 izlenmekte ve optimize edilmektedir.' },
    { icon: '💎', title: 'Kalite', description: 'Full HD ve 4K kalitede yayınlar, yüksek hızlı sunucular ve gelişmiş teknoloji ile en iyi deneyimi sunuyoruz.' },
    { icon: '🤝', title: 'Müşteri Odaklılık', description: '7/24 WhatsApp desteği, hızlı yanıt süreleri ve kişisel yaklaşım ile müşteri memnuniyetini ön planda tutuyoruz.' },
    { icon: '🔒', title: 'Güvenlik', description: '3D Secure ödeme sistemleri, SSL şifreleme ve güvenli veri saklama ile bilgilerinizi koruyoruz.' }
  ];
  const address = contactInfo?.address || { street: 'Friedrichstraße 123', city: 'Berlin', postalCode: '10117', country: 'Almanya' };
  const email = contactInfo?.email || 'elitelk2009@gmail.com';
  const whatsapp = contactInfo?.whatsapp || '+49-163-892-6744';
  const workingHours = contactInfo?.workingHours || '7/24 Açık';

  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Elit IPTV",
      "url": "https://elitiptv.net",
      "logo": "https://elitiptv.net/logo.png",
      "foundingDate": "2020",
      "founder": {
        "@type": "Person",
        "name": "Elit IPTV Team"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": address.street,
        "addressLocality": address.city,
        "postalCode": address.postalCode,
        "addressCountry": address.country === 'Almanya' ? 'DE' : 'TR'
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": whatsapp,
        "contactType": "customer service",
        "email": email,
        "availableLanguage": ["Turkish", "English", "German"]
      },
      "sameAs": [
        "https://play.google.com/store/apps/details?id=com.elitdigitalone.elitdigitaliptvbox"
      ]
    }
  };

  return (
    <div className="about-page">
      <SEO
        title="Hakkımızda | Elit IPTV - Dünya Standartlarında IPTV Hizmeti"
        description="Elit IPTV hakkında: 2020'den beri dünya genelinde hizmet veren premium IPTV sağlayıcısı. 50.000+ kullanıcı, 100+ ülke, %99.9 uptime garantisi. Berlin merkezli, Türkiye ve Avrupa'da özel optimizasyonlar."
        keywords="elit iptv hakkında, iptv şirket, iptv hizmet sağlayıcı, iptv firma, iptv kurumsal, iptv güvenilir, iptv kalite"
        url="/hakkimizda"
        structuredData={aboutStructuredData}
      />
      <Navbar />
      
      <section className="about-hero">
        <div className="hero-background">
          <div className="bg-gradient-animated"></div>
          <div className="floating-shapes">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="floating-shape" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${i * 2}s`,
                animationDuration: `${15 + i * 5}s`
              }}></div>
            ))}
          </div>
        </div>
        <div className="container">
          <h1 className="gradient-text">Hakkımızda</h1>
          <p>Elit IPTV olarak dünya standartlarında eğlence deneyimi sunuyoruz</p>
        </div>
      </section>

      <section className="about-story reveal">
        <div className="container">
          <div className="story-content">
            <h2>Bizim Hikayemiz</h2>
            <p>{story}</p>
            <p>{story2}</p>
            <p>{story3}</p>
          </div>
        </div>
      </section>

      <section className="about-values reveal">
        <div className="container">
          <h2 className="section-title gradient-text">Değerlerimiz</h2>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-contact reveal">
        <div className="container">
          <h2 className="section-title gradient-text">İletişim Bilgilerimiz</h2>
          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-icon">📱</div>
              <h3>WhatsApp</h3>
              <p>7/24 hızlı destek ve bilgi için bizimle hemen iletişime geçin.</p>
              <div className="response-badge">
                <span>⚡</span> Ortalama Yanıt: 2 dakika
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => sendSmartWhatsAppMessage('general', { question: 'Hakkımızda sayfasından iletişime geçiyorum' }, contactInfo)}
              >
                WhatsApp Destek
              </button>
            </div>
            <div className="contact-card">
              <div className="contact-icon">✉️</div>
              <h3>E-posta</h3>
              <p>Kurumsal iletişim ve resmi sorularınız için bize yazabilirsiniz.</p>
              <div className="response-badge">
                <span>📧</span> Ortalama Yanıt: 24 saat
              </div>
              <a href={`mailto:${email}`} className="btn btn-primary">E-posta Gönder</a>
            </div>
            <div className="contact-card">
              <div className="contact-icon">📍</div>
              <h3>Adres</h3>
              <p>{address.city}, {address.country} merkez ofisimiz.</p>
              <div className="address-info">
                <strong>{address.street}</strong>
                <span>{address.postalCode} {address.city}, {address.country}</span>
              </div>
              <button className="btn btn-primary">🌍 Avrupa Merkez Ofis</button>
            </div>
          </div>
        </div>
      </section>

      <section className="working-hours reveal">
        <div className="container">
          <h3>Çalışma Saatleri</h3>
          <p>{workingHours}</p>
          <div className="hours-grid">
            <div className="hour-item">
              <div className="day">Pazartesi - Cuma</div>
              <div className="time">24 Saat Açık</div>
            </div>
            <div className="hour-item">
              <div className="day">Cumartesi</div>
              <div className="time">24 Saat Açık</div>
            </div>
            <div className="hour-item">
              <div className="day">Pazar</div>
              <div className="time">24 Saat Açık</div>
            </div>
          </div>
          <p className="highlight-text">✨ WhatsApp üzerinden 7/24 anında destek alabilirsiniz!</p>
        </div>
      </section>

      <section className="about-faq">
        <div className="container">
          <h2 className="section-title gradient-text">Sık Sorulan Sorular</h2>
          <p className="section-subtitle">Elit IPTV hakkında bilmek istediğiniz her şey</p>
          
          <div className="faq-stats reveal">
            <div className="faq-stat-card">
              <div className="faq-stat-number">11+</div>
              <div className="faq-stat-label">Sık Sorulan Soru</div>
            </div>
            <div className="faq-stat-card">
              <div className="faq-stat-number">7/24</div>
              <div className="faq-stat-label">Destek Hizmeti</div>
            </div>
            <div className="faq-stat-card">
              <div className="faq-stat-number">%99</div>
              <div className="faq-stat-label">Müşteri Memnuniyeti</div>
            </div>
          </div>
          
          <div className="faq-grid reveal">
            <FAQItem question="Paketler nasıl aktif edilir?" answer="Ödemeniz onaylandıktan sonra 5 dakika içinde kullanıcı bilgileriniz WhatsApp üzerinden gönderilir ve hemen izlemeye başlayabilirsiniz." />
            <FAQItem question="Hangi cihazlarda kullanabilirim?" answer="Smart TV, Android TV, telefon, tablet, bilgisayar ve tüm IPTV uyumlu cihazlarda kullanabilirsiniz." />
            <FAQItem question="Ödeme güvenli mi?" answer="Evet, 3D Secure ödeme sistemi ve SSL şifreleme ile güvenli ödeme altyapısı kullanıyoruz." />
            <FAQItem question="Test süresi var mı?" answer="Evet, ücretsiz test hizmeti sunuyoruz. 'Ücretsiz Test Et' butonundan başvurabilirsiniz." />
            <FAQItem question="Kesintisiz yayın garantisi var mı?" answer="Evet, %99.9 uptime garantisi sunuyoruz. Anti-Freeze teknolojimiz ile donma yaşamazsınız." />
            <FAQItem question="İade politikası nedir?" answer="Satın aldığınız pakette teknik sorun yaşarsanız ve çözemediysek, ilk 7 gün içinde iade hakkınız vardır." />
            <FAQItem question="Kaç cihazda aynı anda izleyebilirim?" answer="Standart paketlerde 1 cihaz kullanım hakkı vardır. Çoklu cihaz paketlerimiz mevcuttur." />
            <FAQItem question="Yayın kalitesi nasıl?" answer="Tüm kanallarımız Full HD ve 4K Ultra HD kalitesinde yayınlanmaktadır." />
            <FAQItem question="Kurulum desteği alabilir miyim?" answer="Evet, tüm cihazlarınız için ücretsiz uzaktan kurulum desteği sağlıyoruz." />
            <FAQItem question="Abonelik sürem dolduğunda ne olur?" answer="Süreniz dolmadan 7 gün önce hatırlatma mesajı gönderilir. Yenilemek için WhatsApp'tan iletişime geçebilirsiniz." />
            <FAQItem question="Hangi ülkelerden erişebilirim?" answer="Dünya genelinde tüm ülkelerden erişim sağlayabilirsiniz. Özellikle Türkiye ve Avrupa için optimize edilmiştir." />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Smart TV Installation Guide Modal
const SmartTVGuideModal = ({ isOpen, onClose }) => {
  const { contactInfo } = useContent();
  
  if (!isOpen) return null;

  const handleWhatsApp = () => {
    let message = `*SMART TV KURULUM DESTEĞİ 📺*\n\n`;
    message += `Merhaba, Smart TV'mde kurulum desteği almak istiyorum.\n\n`;
    message += `*Cihaz:* Smart TV\n`;
    message += `*Sayfa:* ${window.location.pathname}\n`;
    message += `*Tarih:* ${new Date().toLocaleDateString('tr-TR')}`;
    
    const whatsappNumber = contactInfo?.whatsapp?.replace(/[^0-9]/g, '') || '491638926744';
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const recommendedApps = [
    { name: 'HotPlayer', icon: '🔥', description: 'Smart TV için optimize edilmiş IPTV oynatıcı' },
    { name: 'BobPlayer', icon: 'B', description: 'Kolay kullanımlı ve hızlı IPTV oynatıcı' },
    { name: 'IBO Player', icon: 'I', description: 'Profesyonel IPTV deneyimi için gelişmiş oynatıcı' },
    { name: 'Diğer Uygulamalar', icon: '📦', description: 'Smart TV uyumlu diğer IPTV oynatıcıları' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content smart-tv-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <div className="modal-header">
          <span className="modal-icon">📺</span>
          <h2 className="modal-title">Smart TV Kurulum Rehberi</h2>
        </div>
        
        <div className="modal-body">
          <p className="modal-description">
            Smart TV'nizde IPTV izlemek için aşağıdaki uygulamalardan birini kullanabilirsiniz. 
            Uygulamayı kurduktan sonra MAC adresinizi bize gönderin.
          </p>

          <div className="recommended-apps-section">
            <h3 className="section-title-small">Önerilen Uygulamalar</h3>
            <div className="apps-grid-modal">
              {recommendedApps.map((app, index) => (
                <div key={index} className="app-card-modal">
                  <div className="app-icon-modal">{app.icon}</div>
                  <h4 className="app-name-modal">{app.name}</h4>
                  <p className="app-description-modal">{app.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mac-address-section">
            <h3 className="section-title-small">MAC Adresi Formatı</h3>
            <div className="mac-example">
              <div className="mac-label">MAC Adresi Örneği:</div>
              <div className="mac-value">00:*****78</div>
            </div>
            <p className="mac-note">
              MAC adresinizin tamamını göndermeniz gerekmektedir.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary btn-full btn-whatsapp" onClick={handleWhatsApp}>
            <span>💬</span> WhatsApp Destek
          </button>
        </div>
      </div>
    </div>
  );
};

// Kurulum Page
const Kurulum = () => {
  const { links, contactInfo } = useContent();
  const [isSmartTVModalOpen, setIsSmartTVModalOpen] = useState(false);
  const [currentDeviceIcon, setCurrentDeviceIcon] = useState(0);
  
  // Default links (fallback)
  const defaultLinks = {
    windowsPC: 'https://tinyurl.com/elitpc1',
    androidXC: 'https://tinyurl.com/elitmedia1',
    androidSmarters: 'https://tinyurl.com/elitmedia2',
    playStoreXC: 'https://play.google.com/store/apps/details?id=com.nathnetwork.elitplus',
    playStoreSmarters: 'https://play.google.com/store/apps/details?id=com.elitdigitalone.elitdigitaliptvbox',
    iosApp: 'https://apps.apple.com/us/app/000-player/id1665441224'
  };
  const appLinks = links || defaultLinks;
  
  const deviceIcons = ['📱', '💻', '📺', '📱', '💻', '📱', '📺', '💻', '📱', '💻', '📺'];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDeviceIcon((prev) => (prev + 1) % deviceIcons.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [deviceIcons.length]);

  const installationStructuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Elit IPTV Kurulum Rehberi",
    "description": "Smart TV, Android, iOS, Windows ve tüm cihazlarda Elit IPTV kurulum rehberi. 5 dakikada kurulum, ücretsiz destek.",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Uygulama İndir",
        "text": "Cihazınıza uygun uygulamayı indirin"
      },
      {
        "@type": "HowToStep",
        "name": "Giriş Yap",
        "text": "Size gönderilen bilgilerle giriş yapın"
      },
      {
        "@type": "HowToStep",
        "name": "Ayarları Yap",
        "text": "Gerekirse ayarları özelleştirin"
      },
      {
        "@type": "HowToStep",
        "name": "İzlemeye Başla",
        "text": "Artık izlemeye hazırsınız!"
      }
    ]
  };

  return (
    <div className="installation-page">
      <SEO
        title="IPTV Kurulum Rehberi | Elit IPTV - Tüm Cihazlarda Kurulum"
        description="Elit IPTV kurulum rehberi: Smart TV, Android, iOS, Windows PC kurulum adımları. 5 dakikada kurulum, ücretsiz teknik destek, 7/24 yardım. Hemen kuruluma başlayın!"
        keywords="iptv kurulum, smart tv iptv kurulum, android iptv kurulum, iptv nasıl kurulur, iptv kurulum rehberi, iptv kurulum desteği, iptv uygulama kurulumu"
        url="/kurulum"
        structuredData={installationStructuredData}
      />
      <Navbar />
      
      <section className="installation-hero">
        <div className="hero-background">
          <div className="bg-gradient-animated"></div>
          <div className="floating-shapes">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="floating-shape" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${i * 2}s`,
                animationDuration: `${15 + i * 5}s`
              }}></div>
            ))}
          </div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon rotating-icon">{deviceIcons[currentDeviceIcon]}</span>
              5 Dakikada Kurulum
            </div>
            <h1 className="hero-title">
              Tüm cihazlarınızda Elit IPTV'yi <span className="gradient-text">kolayca kurun</span> ve izlemeye başlayın.
            </h1>
            <p className="hero-description">
              Android, iOS, Smart TV, Windows ve tüm cihazlarınız için kolay kurulum. 
              Ücretsiz teknik destek ve 7/24 canlı yardım ile hiçbir sorun yaşamadan izlemeye başlayın.
            </p>
            <div className="hero-features">
              <div className="hero-feature-item">
                <span className="feature-icon">📱</span>
                <span>Telefon</span>
              </div>
              <div className="hero-feature-item">
                <span className="feature-icon">💻</span>
                <span>PC</span>
              </div>
              <div className="hero-feature-item">
                <span className="feature-icon">📱</span>
                <span>Tablet</span>
              </div>
              <div className="hero-feature-item">
                <span className="feature-icon">📺</span>
                <span>TV</span>
              </div>
            </div>
            <div className="hero-cta-buttons">
              <button 
                className="btn btn-primary btn-large"
                onClick={() => sendSmartWhatsAppMessage('installation', {}, contactInfo)}
              >
                <span>💬</span> WhatsApp ile Kurulum Desteği Al
              </button>
              <a href="#platforms" className="btn btn-secondary btn-large">
                <span>📱</span> Cihaz Seç ve Kuruluma Başla
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="platforms" className="platforms-section">
        <div className="container">
          <div className="section-header reveal">
            <h2 className="section-title gradient-text">Cihazınıza Göre Uygulama Seçin</h2>
            <p className="section-subtitle">Güvenliğiniz için sadece resmi mağazalardaki uygulamalarımızı kullanın.</p>
          </div>
          
          <div className="platforms-grid reveal">
            <div className="platform-card">
              <div className="platform-icon" style={{fontSize: '5rem'}}>🤖</div>
              <h3>Android & TV</h3>
              <p>Tüm Android telefon, tablet ve Android TV Box cihazlarınız için en stabil seçenek.</p>
              <div className="platform-apps">
                <a href={appLinks.playStoreSmarters} target="_blank" rel="noopener noreferrer" className="app-download-btn" style={{background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'}}>
                  <span className="app-name">ELIT Smarters</span>
                  <span className="app-store">Google Play</span>
                </a>
                <a href={appLinks.androidSmarters} target="_blank" rel="noopener noreferrer" className="app-download-btn" style={{background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'}}>
                  <span className="app-name">ELIT Smarters</span>
                  <span className="app-store">Harici İndir</span>
                </a>
                <a href={appLinks.playStoreXC} target="_blank" rel="noopener noreferrer" className="app-download-btn" style={{background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'}}>
                  <span className="app-name">ELIT XC</span>
                  <span className="app-store">Google Play</span>
                </a>
                <a href={appLinks.androidXC} target="_blank" rel="noopener noreferrer" className="app-download-btn" style={{background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'}}>
                  <span className="app-name">ELIT XC</span>
                  <span className="app-store">Harici İndir</span>
                </a>
              </div>
            </div>
            
            <div className="platform-card">
              <div className="platform-icon" style={{fontSize: '5rem'}}>🍎</div>
              <h3>iOS & macOS</h3>
              <p>iPhone, iPad, Apple TV ve Mac bilgisayarlarınız için profesyonel oynatıcı.</p>
              <div className="platform-apps">
                <a href={appLinks.iosApp} target="_blank" rel="noopener noreferrer" className="app-download-btn" style={{background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'}}>
                  <span className="app-name">000 Player</span>
                  <span className="app-store">App Store</span>
                </a>
              </div>
            </div>
            
            <div className="platform-card">
              <div className="platform-icon" style={{fontSize: '5rem'}}>💻</div>
              <h3>Windows PC</h3>
              <p>Windows 10 ve 11 bilgisayarlarınızdan donmadan izleme keyfi için özel uygulama.</p>
              <div className="platform-apps">
                <a href={appLinks.windowsPC} target="_blank" rel="noopener noreferrer" className="app-download-btn" style={{background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'}}>
                  <span className="app-name">Elite++</span>
                  <span className="app-store">İndir</span>
                </a>
              </div>
            </div>
            
            <div className="platform-card">
              <div className="platform-icon" style={{fontSize: '5rem'}}>📺</div>
              <h3>Smart TV</h3>
              <p>Samsung, LG, Sony ve diğer Smart TV'leriniz için özel kurulum.</p>
              <button 
                className="guide-btn" 
                style={{borderColor: '#EF4444', color: '#EF4444'}}
                onClick={() => setIsSmartTVModalOpen(true)}
              >
                Kurulum Rehberi
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="quick-steps-section">
        <div className="container">
          <div className="section-header reveal">
            <h2 className="section-title gradient-text">Hızlı Kurulum Adımları</h2>
            <p className="section-subtitle">Genel kurulum süreci hakkında bilgi</p>
          </div>
          
          <div className="quick-steps-grid reveal">
            <div className="quick-step-card">
              <div className="step-number">1</div>
              <div className="step-icon">📥</div>
              <h4>Uygulama İndir</h4>
              <p>Cihazınıza uygun uygulamayı indirin</p>
            </div>
            <div className="quick-step-card">
              <div className="step-number">2</div>
              <div className="step-icon">🔐</div>
              <h4>Giriş Yap</h4>
              <p>Size gönderilen bilgilerle giriş yapın</p>
            </div>
            <div className="quick-step-card">
              <div className="step-number">3</div>
              <div className="step-icon">⚙️</div>
              <h4>Ayarları Yap</h4>
              <p>Gerekirse ayarları özelleştirin</p>
            </div>
            <div className="quick-step-card">
              <div className="step-number">4</div>
              <div className="step-icon">🎉</div>
              <h4>İzlemeye Başla</h4>
              <p>Artık izlemeye hazırsınız!</p>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <div className="section-header reveal">
            <h2 className="section-title gradient-text">Sık Sorulan Sorular</h2>
            <p className="section-subtitle">Kurulum hakkında merak ettikleriniz</p>
          </div>
          
          <div className="faq-grid reveal">
            <FAQItem 
              question="Hangi cihazlarda izleyebilirim?" 
              answer="Smart TV, Android TV, Android telefon ve tablet, iPhone, iPad, Apple TV, Windows PC, Mac, Amazon Fire Stick ve tüm IPTV uyumlu cihazlarda izleyebilirsiniz." 
            />
            <FAQItem 
              question="Kurulum ücretli mi?" 
              answer="Hayır, kurulum tamamen ücretsizdir. Satın aldığınız paket ücretine kurulum dahildir. Ayrıca ücretsiz kurulum desteği sağlıyoruz." 
            />
            <FAQItem 
              question="Teknik destek alabilir miyim?" 
              answer="Evet, 7/24 canlı teknik destek ekibimiz tüm cihazlarınız için uzaktan kurulum ve sorun çözme desteği sağlar." 
            />
            <FAQItem 
              question="Birden fazla cihazda kullanabilir miyim?" 
              answer="Standart paketlerde 1 cihaz kullanım hakkı vardır. Birden fazla cihazda kullanmak için çoklu kullanıcı paketlerimizi tercih edebilirsiniz." 
            />
          </div>
        </div>
      </section>

      <section className="support-cta">
        <div className="container">
          <div className="support-card reveal">
            <div className="support-icon">💬</div>
            <h3>Yardıma mı ihtiyacınız var?</h3>
            <p>Kurulum konusunda destek almak için WhatsApp üzerinden bizimle iletişime geçin.</p>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => sendSmartWhatsAppMessage('installation', { question: 'Kurulum sayfasından destek talep ediyorum' }, contactInfo)}
            >
              WhatsApp Destek
            </button>
          </div>
        </div>
      </section>

      <SmartTVGuideModal
        isOpen={isSmartTVModalOpen}
        onClose={() => setIsSmartTVModalOpen(false)}
      />

      <Footer />
    </div>
  );
};

// Bayilik Page
const Bayilik = () => {
  const { contactInfo } = useContent();
  const [userCount, setUserCount] = useState(50);
  const [packagePrice, setPackagePrice] = useState(1300);
  const [commission, setCommission] = useState(40);

  const monthlyRevenue = userCount * packagePrice;
  const monthlyProfit = (monthlyRevenue * commission) / 100;
  const yearlyProfit = monthlyProfit * 12;

  const resellerStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "IPTV Reseller Program",
    "provider": {
      "@type": "Organization",
      "name": "Elit IPTV"
    },
    "description": "Elit IPTV bayilik programı. %30-50 komisyon, yönetim paneli, teknik destek. Türkiye ve Avrupa'da bayilik fırsatı.",
    "areaServed": ["TR", "EU"],
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "TRY",
      "description": "Ücretsiz bayilik başvurusu"
    }
  };

  return (
    <div className="reseller-page">
      <SEO
        title="IPTV Bayilik Programı | Elit IPTV - Yüksek Komisyon, Kolay Kazanç"
        description="Elit IPTV bayilik programı ile %30-50 komisyon kazanın. Yönetim paneli, teknik destek, web sitesi desteği. Türkiye ve Avrupa'da bayilik fırsatı. Ücretsiz başvuru!"
        keywords="iptv bayilik, iptv reseller, iptv bayilik programı, iptv komisyon, iptv bayi ol, iptv satış, iptv bayilik şartları, iptv yönetim paneli"
        url="/bayilik"
        structuredData={resellerStructuredData}
      />
      <Navbar />
      
      <section className="reseller-hero">
        <div className="hero-background">
          <div className="bg-gradient-animated"></div>
          <div className="floating-shapes">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="floating-shape" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${i * 2}s`,
                animationDuration: `${15 + i * 5}s`
              }}></div>
            ))}
          </div>
        </div>
        <div className="container">
          <div className="hero-content-wrapper">
            <h1 className="hero-title">Türkiye ve Avrupa'da küçükten başlayarak büyüyerek <span className="highlight-text">güzel paralar kazanın</span></h1>
            <div className="hero-stats-grid">
              <div className="stat-card">
                <div className="stat-number">500+</div>
                <div className="stat-label">AKTİF BAYİ</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">50K+</div>
                <div className="stat-label">TOPLAM KULLANICI</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">%40</div>
                <div className="stat-label">ORTALAMA KOMİSYON</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">24/7</div>
                <div className="stat-label">DESTEK HİZMETİ</div>
              </div>
            </div>
            <div className="hero-cta">
              <button 
                className="btn btn-primary btn-large"
                onClick={() => sendSmartWhatsAppMessage('reseller', {}, contactInfo)}
              >
                Hemen Başvur
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="benefits-section reveal">
        <div className="container">
          <h2 className="section-title gradient-text">Neden Elit IPTV Bayisi Olmalısınız?</h2>
          <p className="section-subtitle">Size özel avantajlar ve fırsatlar</p>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">💰</div>
              <h3>Yüksek Kazanç</h3>
              <p>Türkiye ve Avrupa'da en cazip komisyon oranları ile aylık binlerce TL kazanın.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🔧</div>
              <h3>Tam Destek</h3>
              <p>Teknik altyapı, web sitesi kurulumu ve müşteri yönetimi konularında tam destek.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📊</div>
              <h3>Kolay Yönetim</h3>
              <p>Kullanıcı dostu yönetim paneli ile işinizi kolayca takip edin.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🚀</div>
              <h3>Hızlı Başlangıç</h3>
              <p>Kayıt olduktan sonra 24 saat içinde panel erişiminiz aktif edilir.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💎</div>
              <h3>Kaliteli Altyapı</h3>
              <p>4K kalite, kesintisiz yayın ve 3.99.9 uptime garantisi ile müşterilerinizi memnun edin.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📱</div>
              <h3>Çoklu Platform</h3>
              <p>Tüm cihazlarda çalışan, güvenilir ve hızlı IPTV altyapısı.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="calculator-section reveal">
        <div className="container">
          <h2 className="section-title gradient-text">Kazanç Hesaplayıcı</h2>
          <p className="section-subtitle">Ne kadar kazanabileceğinizi hesaplayın</p>
          
          <div className="earnings-calculator">
            <div className="calculator-card">
              <div className="calculator-inputs">
                <div className="input-group">
                  <label><span className="label-icon">👥</span> Kullanıcı Sayısı</label>
                  <input 
                    type="range" 
                    min="10" 
                    max="500" 
                    value={userCount}
                    onChange={(e) => setUserCount(Number(e.target.value))}
                    className="slider"
                  />
                  <div className="input-value">{userCount} kullanıcı</div>
                </div>
                
                <div className="input-group">
                  <label><span className="label-icon">💰</span> Ortalama Paket Fiyatı (TL)</label>
                  <input 
                    type="range" 
                    min="500" 
                    max="3000" 
                    step="100"
                    value={packagePrice}
                    onChange={(e) => setPackagePrice(Number(e.target.value))}
                    className="slider"
                  />
                  <div className="input-value">{packagePrice} TL</div>
                </div>
                
                <div className="input-group">
                  <label><span className="label-icon">📊</span> Komisyon Oranı (%)</label>
                  <input 
                    type="range" 
                    min="30" 
                    max="50" 
                    value={commission}
                    onChange={(e) => setCommission(Number(e.target.value))}
                    className="slider"
                  />
                  <div className="input-value">%{commission}</div>
                </div>
              </div>
              
              <div className="calculator-results">
                <div className="result-card">
                  <div className="result-label">AYLIK GELİR</div>
                  <div className="result-value">{monthlyRevenue.toLocaleString('tr-TR')} TL</div>
                </div>
                <div className="result-card highlight">
                  <div className="result-label">AYLIK KAZANÇ</div>
                  <div className="result-value">{monthlyProfit.toLocaleString('tr-TR')} TL</div>
                </div>
                <div className="result-card">
                  <div className="result-label">YILLIK KAZANÇ</div>
                  <div className="result-value">{yearlyProfit.toLocaleString('tr-TR')} TL</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="levels-section reveal">
        <div className="container">
          <h2 className="section-title gradient-text">Bayilik Seviyeleri</h2>
          <p className="section-subtitle">Size en uygun seviyeyi seçin</p>
          
          <div className="levels-grid">
            <div className="level-card">
              <h3 className="level-header">Başlangıç</h3>
              <div className="level-commission">30%</div>
              <div className="level-price">Ücretsiz</div>
              <ul className="level-features">
                <li><span className="check-icon">✓</span> 30% Komisyon Oranı</li>
                <li><span className="check-icon">✓</span> Minimum 10 Kullanıcı</li>
                <li><span className="check-icon">✓</span> Yönetim Paneli Erişimi</li>
                <li><span className="check-icon">✓</span> 7/24 Teknik Destek</li>
                <li><span className="check-icon">✓</span> Web Sitesi Desteği</li>
                <li><span className="check-icon">✓</span> 2 Ayda Bir Kredi Zorunluluğu</li>
              </ul>
              <button 
                className="btn btn-primary btn-full"
                onClick={() => sendSmartWhatsAppMessage('reseller', { level: 'Başlangıç' }, contactInfo)}
              >
                Bu Seviyeyi Seç
              </button>
            </div>
            
            <div className="level-card popular">
              <div className="level-badge">EN POPÜLER</div>
              <h3 className="level-header">Profesyonel</h3>
              <div className="level-commission">40%</div>
              <div className="level-price">Ücretsiz</div>
              <ul className="level-features">
                <li><span className="check-icon">✓</span> 40% Komisyon Oranı</li>
                <li><span className="check-icon">✓</span> Minimum 50 Kullanıcı</li>
                <li><span className="check-icon">✓</span> Özel Yönetim Paneli</li>
                <li><span className="check-icon">✓</span> Öncelikli Destek</li>
                <li><span className="check-icon">✓</span> Özel Web Sitesi</li>
                <li><span className="check-icon">✓</span> Marka Desteği</li>
                <li><span className="check-icon">✓</span> Aylık Kredi Zorunluluğu</li>
              </ul>
              <button 
                className="btn btn-primary btn-full"
                onClick={() => sendSmartWhatsAppMessage('reseller', { level: 'Profesyonel' }, contactInfo)}
              >
                Bu Seviyeyi Seç
              </button>
            </div>
            
            <div className="level-card">
              <h3 className="level-header">Kurumsal</h3>
              <div className="level-commission">50%</div>
              <div className="level-price">Özel Fiyat</div>
              <ul className="level-features">
                <li><span className="check-icon">✓</span> 50% Komisyon Oranı</li>
                <li><span className="check-icon">✓</span> Minimum 200 Kullanıcı</li>
                <li><span className="check-icon">✓</span> Özel API Erişimi</li>
                <li><span className="check-icon">✓</span> Dedike Destek Ekibi</li>
                <li><span className="check-icon">✓</span> Özel Marka Çözümleri</li>
                <li><span className="check-icon">✓</span> Toplu İşlem Desteği</li>
                <li><span className="check-icon">✓</span> Özel Anlaşma</li>
              </ul>
              <button 
                className="btn btn-primary btn-full"
                onClick={() => sendSmartWhatsAppMessage('reseller', { level: 'Kurumsal' }, contactInfo)}
              >
                Bu Seviyeyi Seç
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="panel-features-section reveal">
        <div className="container">
          <h2 className="section-title gradient-text">Yönetim Paneli Özellikleri</h2>
          <p className="section-subtitle">Güçlü ve kullanıcı dostu panel ile işinizi kolaylaştırın</p>
          
          <div className="panel-features-grid">
            <div className="panel-feature-card">
              <div className="panel-feature-icon">📊</div>
              <h4>Gerçek Zamanlı İstatistikler</h4>
              <p>Satışlarınız ve kullanıcılarınızı anlık takip edin</p>
            </div>
            <div className="panel-feature-card">
              <div className="panel-feature-icon">💳</div>
              <h4>Otomatik Ödeme Sistemi</h4>
              <p>Müşterileriniz otomatik olarak ödeme yapabilir</p>
            </div>
            <div className="panel-feature-card">
              <div className="panel-feature-icon">👥</div>
              <h4>Kullanıcı Yönetimi</h4>
              <p>Kolay kullanıcı ekleme, silme ve düzenleme</p>
            </div>
            <div className="panel-feature-card">
              <div className="panel-feature-icon">✉️</div>
              <h4>E-posta Bildirimleri</h4>
              <p>Önemli işlemler için otomatik bildirimler</p>
            </div>
            <div className="panel-feature-card">
              <div className="panel-feature-icon">🔒</div>
              <h4>Güvenli Sistem</h4>
              <p>SSL şifreleme ve güvenli ödeme altyapısı</p>
            </div>
            <div className="panel-feature-card">
              <div className="panel-feature-icon">📱</div>
              <h4>Mobil Uyumlu Panel</h4>
              <p>Her yerden panelinize erişin</p>
            </div>
          </div>
        </div>
      </section>

      <section className="requirements-section reveal">
        <div className="container">
          <div className="requirements-card">
            <h2 className="section-title gradient-text">Bayilik Şartları</h2>
            <div className="requirements-grid">
              <div className="requirement-item">
                <div className="requirement-icon">🔔</div>
                <p>2 ayda bir kredi almak zorunludur.</p>
              </div>
              <div className="requirement-item">
                <div className="requirement-icon">👥</div>
                <p>Minimum 10 kullanıcı ile başlangıç yapılır.</p>
              </div>
              <div className="requirement-item">
                <div className="requirement-icon">⚠️</div>
                <p>3 ay kredi almayan bayilikler kapatılır.</p>
              </div>
              <div className="requirement-item">
                <div className="requirement-icon">❤️</div>
                <p>Müşteri memnuniyeti önceliğimizdir.</p>
              </div>
            </div>
            <div className="requirements-cta">
              <button 
                className="btn btn-primary btn-large"
                onClick={() => sendSmartWhatsAppMessage('reseller', { question: 'Bayilik şartları hakkında bilgi almak istiyorum' }, contactInfo)}
              >
                Şimdi Başvur
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section reveal">
        <div className="container">
          <div className="cta-content">
            <h2>Hemen Başlayın, <span className="highlight-text">Kazanmaya Başlayın!</span></h2>
            <p>Binlerce memnun bayimiz arasına katılın ve yüksek kazançlar elde edin.</p>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => sendSmartWhatsAppMessage('reseller', { question: 'Bayilik başvurusu yapmak istiyorum' }, contactInfo)}
            >
              Ücretsiz Başvur Yap
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Packages Page
const Packages = () => {
  const { packages: firestorePackages } = useContent();
  const [activeRegion, setActiveRegion] = useState('tr');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);

  // Default packages (fallback)
  const defaultTrPackages = [
    { id: 1, name: '6 AY PAKET', duration: '6 Ay', price: '1.300', originalPrice: '2.000', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: true, region: 'tr' },
    { id: 2, name: '12 AY PAKET', duration: '12 Ay', price: '2.000', originalPrice: '3.000', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: false, region: 'tr' },
    { id: 3, name: '12 + 3 AY PAKET', duration: '15 Ay', price: '2.500', originalPrice: '3.500', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: false, region: 'tr' },
    { id: 4, name: '15 + 3 AY PAKET', duration: '18 Ay', price: '2.900', originalPrice: '4.000', features: ['55.000+ Kanal & İçerik', '4K Ultra HD Yayın', '7/24 Canlı Destek', 'Tüm Cihazlarla Uyumlu'], popular: false, region: 'tr' }
  ];

  const defaultEuPackages = [
    { id: 5, name: '3 AY PAKET', duration: '3 Ay', price: '€45', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '1 Kullanıcı', popular: true, region: 'eu' },
    { id: 6, name: '6 AY PAKET', duration: '6 Ay', price: '€65', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '1 Kullanıcı', popular: false, region: 'eu' },
    { id: 7, name: '12 AY PAKET', duration: '12 Ay', price: '€100', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '1 Kullanıcı', popular: false, region: 'eu' },
    { id: 8, name: '12 AY PAKET', duration: '12 Ay', price: '€185', features: ['Kesintisiz ve Donmayan Yayın Garantisi', 'Full HD, 4K UHD Yayın Kalitesi', 'Binlerce Canlı TV Kanalı', 'Sürekli Güncellenen Film ve Dizi Arşivi', 'Tüm Spor, Belgesel ve Çocuk Kanalları', '7/24 Anında Teknik Destek', 'Tüm Cihazlarla Uyumlu', 'Anında Aktivasyon'], users: '2 Kullanıcı', popular: false, region: 'eu' }
  ];

  // Use Firestore packages if available, otherwise use defaults
  const trPackagesFromFirestore = firestorePackages.filter(p => p.region === 'tr');
  const euPackagesFromFirestore = firestorePackages.filter(p => p.region === 'eu');
  
  const trPackages = trPackagesFromFirestore.length > 0 ? trPackagesFromFirestore : defaultTrPackages;
  const euPackages = euPackagesFromFirestore.length > 0 ? euPackagesFromFirestore : defaultEuPackages;

  const currentPackages = activeRegion === 'eu' ? euPackages : trPackages;

  const platformLogos = [
    'https://tonrextv.com/wp-content/uploads/2025/05/Dis1.jpg',
    'https://tonrextv.com/wp-content/uploads/2025/05/app1-1.jpg',
    'https://tonrextv.com/wp-content/uploads/2025/05/amaz1-1.jpg',
    'https://tonrextv.com/wp-content/uploads/2025/05/netf1-1.jpg',
    'https://tonrextv.com/wp-content/uploads/2025/05/hul1-1.jpg'
  ];

  const packagesStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Elit IPTV Paketleri",
    "description": "TR ve EU paketleri. 6 ay, 12 ay ve özel paket seçenekleri. 55.000+ kanal, 4K kalite, 7/24 destek.",
    "brand": {
      "@type": "Brand",
      "name": "Elit IPTV"
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "6 AY PAKET",
        "price": "1300",
        "priceCurrency": "TRY",
        "availability": "https://schema.org/InStock",
        "url": "https://elitiptv.net/paketler"
      },
      {
        "@type": "Offer",
        "name": "12 AY PAKET",
        "price": "2000",
        "priceCurrency": "TRY",
        "availability": "https://schema.org/InStock",
        "url": "https://elitiptv.net/paketler"
      }
    ]
  };

  return (
    <div className="packages-page">
      <SEO
        title="IPTV Paketleri ve Fiyatları | Elit IPTV - En Uygun Fiyatlar"
        description="Elit IPTV paketleri: TR paketleri 1.300 TL'den başlayan fiyatlarla. EU paketleri €45'dan başlıyor. 6 ay, 12 ay ve özel paket seçenekleri. 55.000+ kanal, 4K kalite, tüm cihazlarda uyumlu. Hemen paket seçin!"
        keywords="iptv paket, iptv fiyat, iptv abonelik, iptv paket fiyatları, tr iptv paket, eu iptv paket, iptv 6 ay, iptv 12 ay, ucuz iptv, iptv paket seçenekleri"
        url="/paketler"
        structuredData={packagesStructuredData}
      />
      <Navbar />
      
      <section className="packages-hero">
        <div className="hero-background">
          <div className="bg-gradient-animated"></div>
          <div className="floating-shapes">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="floating-shape" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${i * 3}s`,
                animationDuration: `${20 + i * 5}s`
              }}></div>
            ))}
          </div>
        </div>
        <div className="container">
          <h1 className="gradient-text">Premium Paketler</h1>
          <p>Size en uygun paketi seçin, kesintisiz eğlencenin tadını çıkarın.</p>
        </div>
      </section>

      <section className="platforms-carousel">
        <div className="carousel-wrapper">
          <div className="carousel-track">
            {[...platformLogos, ...platformLogos, ...platformLogos, ...platformLogos].map((logo, index) => (
              <img 
                key={index} 
                src={logo} 
                alt={`Platform ${index + 1}`}
                loading="lazy"
                decoding="async"
              />
            ))}
          </div>
        </div>
      </section>

      <main className="container packages-main">
        <div className="region-selector reveal">
          <button
            className={`region-btn ${activeRegion === 'tr' ? 'active' : ''}`}
            onClick={() => setActiveRegion('tr')}
          >
            TR Paketleri
          </button>
          <button
            className={`region-btn ${activeRegion === 'eu' ? 'active' : ''}`}
            onClick={() => setActiveRegion('eu')}
          >
            EU Paketleri
          </button>
        </div>

        <div className="packages-grid reveal">
          {currentPackages.map((pkg) => (
            <div key={pkg.id} className={`package-card ${pkg.popular ? 'popular' : ''}`}>
              {pkg.popular && (
                <div className="package-badge">EN ÇOK TERCİH EDİLEN</div>
              )}
              <h3>{pkg.name}</h3>
              <div className="package-duration">{pkg.duration}</div>
              {pkg.users && <div className="package-duration">{pkg.users}</div>}
              <div className="package-price-wrapper">
                <div className="package-price">{pkg.price}<span>{activeRegion === 'tr' ? 'TL' : ''}</span></div>
                {pkg.originalPrice && <div className="package-original-price">{pkg.originalPrice} TL</div>}
              </div>
              {pkg.features && (
                <ul className="package-features">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              )}
              <button 
                className="btn btn-primary btn-full"
                onClick={() => {
                  setSelectedPackage(pkg);
                  setIsTestMode(false);
                  setIsModalOpen(true);
                }}
              >
                WhatsApp'tan Satın Al
              </button>
            </div>
          ))}
        </div>
      </main>

      <OrderModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPackage(null);
        }}
        packageInfo={selectedPackage}
        isTest={isTestMode}
      />

      <section className="faq-section">
        <div className="container">
          <div className="faq-header">
            <h2>Sık Sorulan Sorular</h2>
            <p>Aklınıza takılan tüm soruların cevapları burada.</p>
          </div>
          
          <div className="faq-grid">
            <FAQItem 
              question="Nasıl satın alabilirim?" 
              answer="Dilediğiniz paketin altındaki 'WhatsApp'tan Satın Al' butonuna tıklayarak bizimle iletişime geçebilirsiniz. Ödeme bilgilerinizi WhatsApp üzerinden paylaşacağız." 
            />
            <FAQItem 
              question="Hangi cihazlarda izleyebilirim?" 
              answer="Smart TV, Android TV, Android telefon ve tablet, iPhone, iPad, Apple TV, Windows PC, Mac, Amazon Fire Stick ve tüm IPTV uyumlu cihazlarda sorunsuz izleyebilirsiniz." 
            />
            <FAQItem 
              question="Yayın kalitesi nasıl?" 
              answer="Tüm kanallarımız Full HD ve 4K Ultra HD seçenekleriyle sunulmaktadır. Anti-Freeze teknolojimiz sayesinde donma ve takılma yaşamazsınız." 
            />
            <FAQItem 
              question="Kurulum desteği veriyor musunuz?" 
              answer="Evet, satın alım sonrası tüm cihazlarınız için ücretsiz uzaktan kurulum desteği sağlıyoruz. 7/24 canlı destek ekibimiz her zaman yanınızda." 
            />
            <FAQItem 
              question="Test süresi var mı?" 
              answer="Evet, ücretsiz test hizmeti sunuyoruz. 'Ücretsiz Test Et' butonuna tıklayarak deneme sürümü alabilirsiniz." 
            />
            <FAQItem 
              question="Kaç cihazda kullanabilirim?" 
              answer="Standart paketlerde 1 cihaz kullanım hakkı bulunmaktadır. Çoklu cihaz kullanımı için özel paketlerimiz mevcuttur." 
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// App
function App() {
  return (
    <HelmetProvider>
      <ContentProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/paketler" element={<Packages />} />
            <Route path="/bayilik" element={<Bayilik />} />
            <Route path="/kurulum" element={<Kurulum />} />
            <Route path="/hakkimizda" element={<Hakkimizda />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/callback" element={<AdminCallback />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </Router>
      </ContentProvider>
    </HelmetProvider>
  );
}

export default App;
